import { R4 } from "@ahryman40k/ts-fhir-types";
import cloneDeep from "lodash/cloneDeep";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { fetchDocumentReferences, fetchFhirDocumentsByUrl, uploadDocument } from "@/api/documents";
import {
  aggregateVaccinationRecord,
  bundleToBlob,
  getAuthorRoleFromReference,
} from "@/components/vaccinations/utils";
import {
  addAuthorRoleToVaccinationResources,
  addEntryIfNotExists,
  getUuidUrnReferenceValue,
  makeEntry,
} from "@/components/vaccinations/utils/fhir";
import config from "@/config";
import { FormatCode, Role } from "@/global";
import { OriginalProviderRole } from "@/valueSets/ValueSetEnums";

import Store from "../index";
import AccountModule from "./account";
import DocumentModule from "./document";

const accountState = getModule(AccountModule);
const documentState = getModule(DocumentModule);

async function createVaccinationRecord(documents: R4.IBundle[]): Promise<R4.IBundle> {
  const vaccinationRecord = await aggregateVaccinationRecord(cloneDeep(documents));

  const composition = vaccinationRecord.entry![0].resource as R4.IComposition;
  const patient = accountState.patient;

  composition.subject = {
    reference: getUuidUrnReferenceValue(patient),
  };

  addEntryIfNotExists(vaccinationRecord, makeEntry(patient));

  const author = accountState.author;
  if (author) {
    composition.author = [
      {
        reference: getUuidUrnReferenceValue(author),
      },
    ];
    addEntryIfNotExists(vaccinationRecord, makeEntry(author));
  }

  return vaccinationRecord;
}

@Module({
  dynamic: true,
  store: Store,
  name: "vaccination",
  namespaced: true,
})
export default class VaccinationModule extends VuexModule {
  fetching: boolean = false;
  uploading: boolean = false;
  vaccinationRecord: R4.IBundle | null = null;
  documents: R4.IBundle[] = [];

  @Mutation
  setFetching(fetching: boolean) {
    this.fetching = fetching;
  }

  @Mutation
  setUploading(uploading: boolean) {
    this.uploading = uploading;
  }

  @Mutation
  clearDocuments() {
    this.documents = [];
    this.vaccinationRecord = null;
  }

  @Mutation
  setDocuments(documents: R4.IBundle[]) {
    this.documents = documents;
  }

  @Mutation
  setVaccinationRecord(bundle: R4.IBundle | null) {
    this.vaccinationRecord = bundle;
  }

  @Action({ rawError: true })
  async fetchDocuments(force: boolean = false) {
    if ((this.vaccinationRecord && !force) || this.fetching) {
      return;
    }

    this.setFetching(true);

    const auth = await accountState.getAuthenticationHeader();
    if (!auth) {
      throw new Error("Failed to get authorization");
    }

    try {
      const query = `format=2.16.756.5.30.1.127.3.10.10|${FormatCode.ImmunizationAdministration}`;
      const documentRefs = await fetchDocumentReferences(query, auth);

      const authorRoleByEntryId = new Map<string, OriginalProviderRole>();
      const urls: string[] = [];

      documentRefs.forEach((reference) => {
        const binaryRef = reference.content[0].attachment.url!.replace(config.r4.base, "");
        urls.push(binaryRef);

        // Store the author role for each document reference to later add it the
        // FHIR document content
        const [, base64Id] = binaryRef.split("/");
        const [entryId] = atob(base64Id).split("@");
        const authorRole = getAuthorRoleFromReference(reference);
        if (authorRole) {
          authorRoleByEntryId.set(entryId, authorRole);
        }
      });

      const documentsBundle = await fetchFhirDocumentsByUrl(urls, auth);

      const documents = (documentsBundle?.entry || [])
        .filter((entry) => entry.resource?.resourceType === "Bundle")
        .map((entry) => {
          const authorRole = entry.id && authorRoleByEntryId.get(entry.id);
          if (!authorRole) {
            return entry.resource as R4.IBundle;
          }

          const bundle = entry.resource as R4.IBundle;
          addAuthorRoleToVaccinationResources(bundle, authorRole);

          return bundle;
        });

      // Filter out Binary resources before storing the documents in the state
      documents.forEach((bundle) => {
        const composition = bundle.entry?.find(
          (entry) => entry.resource?.resourceType === "Composition"
        )?.resource as R4.IComposition;
        composition.section = composition.section?.filter(
          (section) => !section.entry?.some((entry) => entry.reference?.startsWith("Binary/"))
        );
        bundle.entry = bundle.entry?.filter((entry) => entry.resource?.resourceType !== "Binary");
      });

      this.setDocuments(documents);
      const vaccinationRecord = await createVaccinationRecord(documents);
      this.setVaccinationRecord(vaccinationRecord);
      this.setFetching(false);
    } catch (e) {
      this.setFetching(false);
      throw e;
    }
  }

  @Action({ rawError: true })
  async uploadDocument(document: { reference: R4.IDocumentReference; bundle: R4.IBundle }) {
    const auth = await accountState.getAuthenticationHeader();
    if (!auth) {
      throw new Error("Failed to get authorization");
    }

    const formatCode = document.reference.content[0].format?.code;

    // Optimistically add new immunization administrations documents to the
    // application state for instant feedback.
    if (formatCode === FormatCode.ImmunizationAdministration) {
      // Add the original provider role to a copy to prevent hash mismatches
      const bundleCopy = cloneDeep(document.bundle);
      if (accountState.isProfessionalUser) {
        // TODO: differentiate between HCP and ASS
        addAuthorRoleToVaccinationResources(bundleCopy, OriginalProviderRole.HCP);
      }

      this.documents.push(bundleCopy);
      const vaccinationRecord = await createVaccinationRecord(this.documents);
      this.setVaccinationRecord(vaccinationRecord);
    }

    try {
      this.setUploading(true);
      const blob = bundleToBlob(document.bundle);
      // TODO: use correct role
      const resultRef = await uploadDocument(document.reference, blob, Role.Patient, auth);
      this.setUploading(false);

      // Only add full vaccination records to the state to minimize document queries.
      // Single immunization administrations are hidden by default anyway.
      if (documentState.fetched && formatCode === FormatCode.VaccinationRecord) {
        documentState.addNewEntry(resultRef);
      }
    } catch (e) {
      if (document.reference.content[0].format?.code === FormatCode.ImmunizationAdministration) {
        this.documents.pop();
        const vaccinationRecord = await createVaccinationRecord(this.documents);
        this.setVaccinationRecord(vaccinationRecord);
      }
      this.setUploading(false);
      throw e;
    }
  }
}
